import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, Search, Mail, Building2, 
  Briefcase, Filter, Download, UserCheck
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoundersDirectory = () => {
  const navigate = useNavigate();
  const [founders, setFounders] = useState([]);
  const [filteredFounders, setFilteredFounders] = useState([]);
  const [stats, setStats] = useState({ total_founders: 0, by_industry: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [industryFilter, setIndustryFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterFounders();
  }, [founders, searchTerm, industryFilter]);

  const fetchData = async () => {
    try {
      const [foundersRes, statsRes] = await Promise.all([
        axios.get(`${API}/founders`),
        axios.get(`${API}/founders/stats`)
      ]);
      setFounders(foundersRes.data);
      setFilteredFounders(foundersRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const filterFounders = () => {
    let filtered = [...founders];
    
    if (searchTerm) {
      filtered = filtered.filter(f => 
        f.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (industryFilter !== "all") {
      filtered = filtered.filter(f => f.industry === industryFilter);
    }
    
    setFilteredFounders(filtered);
  };

  const getIndustryColor = (industry) => {
    const colors = {
      "Fintech": "bg-blue-600",
      "Banking": "bg-indigo-600",
      "Healthcare": "bg-red-600",
      "Real Estate": "bg-amber-600",
      "Technology": "bg-purple-600",
      "Retail": "bg-pink-600",
      "Conglomerate": "bg-gray-600",
      "E-commerce": "bg-green-600"
    };
    return colors[industry] || "bg-gray-600";
  };

  const exportToCSV = () => {
    const headers = ["First Name", "Last Name", "Email", "Company", "Title", "Industry"];
    const rows = filteredFounders.map(f => [
      f.first_name, f.last_name, f.email, f.company, f.title, f.industry
    ]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ski_axis_founders.csv";
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAF7F0' }}>
        <div className="text-center">
          <div style={{ width: 48, height: 48, border: '3px solid rgba(200,168,75,0.15)', borderTopColor: '#C8A84B', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#3A4A5C', fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600 }}>Loading founders directory…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAF7F0', fontFamily: "'DM Sans', sans-serif" }}>
      <Header />

      {/* Hero */}
      <div style={{ position: 'relative', padding: '70px 32px 48px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(200,168,75,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(200,168,75,.025) 1px,transparent 1px)', backgroundSize: '80px 80px' }} />
        <div style={{ position: 'absolute', top: '-30%', right: '-15%', width: '70%', height: '140%', background: 'radial-gradient(ellipse at 70% 40%,rgba(200,168,75,.07) 0%,transparent 60%)' }} />
        <div className="container mx-auto px-6" style={{ position: 'relative', zIndex: 5 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.3em', textTransform: 'uppercase', color: '#C8A84B', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ display: 'inline-block', width: 32, height: 1, background: '#C8A84B', opacity: .6 }} />
            Founders Directory
          </div>
          <h1 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 'clamp(36px,5vw,64px)', fontWeight: 700, color: '#0D1B2A', marginBottom: 12, letterSpacing: '-.02em', lineHeight: 1 }}>
            Operators backing <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>SKI AXIS.</em>
          </h1>
          <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 19, fontWeight: 300, fontStyle: 'italic', color: '#3A4A5C', maxWidth: 680, lineHeight: 1.55 }}>
            Contact database for outreach — every founder, every company, every industry inside the platform.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card style={{ background: 'linear-gradient(135deg, rgba(200,168,75,0.15) 0%, rgba(255,255,255,1) 100%)', border: '1px solid rgba(200,168,75,0.4)', borderRadius: 0 }} data-testid="stat-total">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p style={{ color: '#C8A84B', fontSize: 11, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase' }}>Total Contacts</p>
                  <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 44, fontWeight: 700, color: '#0D1B2A', lineHeight: 1, marginTop: 4 }}>{stats.total_founders}</p>
                </div>
                <UserCheck className="h-10 w-10" style={{ color: '#C8A84B' }} />
              </div>
            </CardContent>
          </Card>
          
          {Object.entries(stats.by_industry || {}).slice(0, 3).map(([industry, count], idx) => (
            <Card key={idx} style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)', borderRadius: 0 }} data-testid={`stat-${industry}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p style={{ color: '#7A8190', fontSize: 11, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase' }}>{industry}</p>
                    <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 36, fontWeight: 700, color: '#0D1B2A', lineHeight: 1, marginTop: 4 }}>{count}</p>
                  </div>
                  <Badge style={{ background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.35)', color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase' }}>{industry.slice(0, 3)}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Industry Breakdown */}
        <Card style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)', borderRadius: 0 }} className="mb-8" data-testid="industry-breakdown">
          <CardHeader>
            <CardTitle style={{ color: '#0D1B2A', fontFamily: "'Cormorant Display', serif", fontSize: 22, fontWeight: 700, letterSpacing: '-.01em' }} className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" style={{ color: '#C8A84B' }} />
              Industry Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.by_industry || {}).map(([industry, count]) => (
                <div 
                  key={industry}
                  className="flex items-center gap-2 cursor-pointer transition-colors px-4 py-2"
                  style={{
                    background: industryFilter === industry ? 'rgba(200,168,75,0.12)' : '#07070A',
                    border: `1px solid ${industryFilter === industry ? 'rgba(200,168,75,0.4)' : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: 4
                  }}
                  onClick={() => setIndustryFilter(industryFilter === industry ? "all" : industry)}
                >
                  <Badge style={{ background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.35)', color: '#C8A84B', fontSize: 11, fontWeight: 700 }}>{count}</Badge>
                  <span style={{ fontSize: 13, color: industryFilter === industry ? '#FFFFFF' : '#B8B0A4', fontWeight: industryFilter === industry ? 600 : 400 }}>
                    {industry}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Founders Table */}
        <Card style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)', borderRadius: 0 }} data-testid="founders-table">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle style={{ color: '#0D1B2A', fontFamily: "'Cormorant Display', serif", fontSize: 22, fontWeight: 700, letterSpacing: '-.01em' }} className="flex items-center gap-2">
                  <Users className="h-5 w-5" style={{ color: '#C8A84B' }} />
                  Contact List
                </CardTitle>
                <CardDescription style={{ color: '#7A8190', fontSize: 12, letterSpacing: '.06em', marginTop: 4 }}>
                  {filteredFounders.length} of {founders.length} contacts
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: '#7A8190' }} />
                  <Input 
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-48"
                    style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.4)', color: '#0D1B2A' }}
                    data-testid="search-input"
                  />
                </div>
                <Select value={industryFilter} onValueChange={setIndustryFilter}>
                  <SelectTrigger className="w-40" style={{ background: '#FAF7F0', border: '1px solid rgba(200,168,75,0.4)', color: '#0D1B2A' }} data-testid="industry-filter">
                    <SelectValue placeholder="Industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Industries</SelectItem>
                    {Object.keys(stats.by_industry || {}).map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={exportToCSV}
                  style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, border: 'none', padding: '0 22px' }}
                  data-testid="export-btn"
                >
                  <Download className="h-3 w-3 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(200,168,75,0.3)' }}>
                    <th style={{ color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', textAlign: 'left', padding: '14px 16px' }}>Name</th>
                    <th style={{ color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', textAlign: 'left', padding: '14px 16px' }}>Company</th>
                    <th style={{ color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', textAlign: 'left', padding: '14px 16px' }}>Title</th>
                    <th style={{ color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', textAlign: 'left', padding: '14px 16px' }}>Industry</th>
                    <th style={{ color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', textAlign: 'left', padding: '14px 16px' }}>Email</th>
                    <th style={{ color: '#C8A84B', fontSize: 10, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', textAlign: 'center', padding: '14px 16px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFounders.map((founder, idx) => (
                    <tr 
                      key={idx} 
                      style={{ borderBottom: '1px solid rgba(13,27,42,0.05)', transition: 'background .2s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(200,168,75,0.04)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      data-testid={`founder-row-${idx}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold" style={{ background: 'linear-gradient(135deg, #C8A84B 0%, #D9BC6A 100%)', color: '#07070A', fontFamily: "'Cormorant Display', serif", fontSize: 14, letterSpacing: '.04em' }}>
                            {founder.first_name?.[0]}{founder.last_name?.[0]}
                          </div>
                          <div>
                            <p style={{ color: '#0D1B2A', fontWeight: 600, fontSize: 14 }}>{founder.first_name} {founder.last_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" style={{ color: '#7A8190' }} />
                          <span style={{ color: '#0D1B2A', fontSize: 14 }}>{founder.company}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4" style={{ color: '#3A4A5C', fontSize: 13 }}>{founder.title}</td>
                      <td className="py-4 px-4">
                        <Badge style={{ background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.35)', color: '#C8A84B', fontSize: 11, fontWeight: 600, letterSpacing: '.06em' }}>
                          {founder.industry}
                        </Badge>
                      </td>
                      <td className="py-4 px-4" style={{ color: '#3A4A5C', fontSize: 13 }}>{founder.email}</td>
                      <td className="py-4 px-4 text-center">
                        <a 
                          href={`mailto:${founder.email}`}
                          className="inline-flex items-center gap-1 text-sm"
                          style={{ color: '#C8A84B', fontWeight: 600, letterSpacing: '.04em' }}
                        >
                          <Mail className="h-3 w-3" />
                          Email
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default FoundersDirectory;
